import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';

interface IntegrationGroup {
  group_id: string;
  group_name: string;
}

interface ExecutiveDashboardData {
  keyMetrics: {
    totalApplications: number;
    totalModels: number;
    totalGroups: number;
    totalRunRate: number;
    completedAssessments: number;
    deployedApps: number;
    expectedSavings2Yr: number;
  };
  portfolioByDisposition: Array<{
    disposition: string;
    app_count: string;
  }>;
  aprModelsSummary: Array<{
    id: string;
    model_name: string;
    status: string;
    created_at: string;
    app_count: string;
    qa_completed: string;
    disposition_completed: string;
    in_development: string;
    deployed: string;
  }>;
  integrationGroupsSummary: Array<{
    group_id: string;
    group_name: string;
    status: string;
    app_count: string;
    total_run_rate: string;
    deployed_count: string;
    in_dev_count: string;
  }>;
  benefitSummary: Array<{
    grouping_id: string;
    group_name: string;
    app_count: string;
    total_tco: string;
    total_frc: string;
    total_dc: string;
    total_2yr_savings: string;
    roi_percentage: string;
  }>;
  assessmentByDimension: Array<{
    dimension_name: string;
    total_apps: string;
    completed_apps: string;
    avg_score: string;
  }>;
  topApplications: Array<{
    id: string;
    application_name: string;
    application_id_code: string;
    recommendation: string;
    annual_run_rate: string;
    assessment_status: string;
  }>;
}

// Donut Chart Component
const DonutChart: React.FC<{
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
  centerValue?: string | number;
}> = ({ data, size = 160, strokeWidth = 24, centerLabel, centerValue }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  let currentOffset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        {/* Data segments */}
        {data.map((item, index) => {
          const percentage = total > 0 ? item.value / total : 0;
          const strokeDasharray = `${percentage * circumference} ${circumference}`;
          const strokeDashoffset = -currentOffset * circumference;
          currentOffset += percentage;

          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'all 0.5s ease' }}
            />
          );
        })}
      </svg>
      {/* Center text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1f2937' }}>{centerValue}</div>
        <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>{centerLabel}</div>
      </div>
    </div>
  );
};

// Progress Ring Component
const ProgressRing: React.FC<{
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  label?: string;
}> = ({ percentage, size = 60, strokeWidth = 6, color = '#3b82f6', label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151'
        }}>
          {percentage}%
        </div>
      </div>
      {label && <div style={{ fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>{label}</div>}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [data, setData] = useState<ExecutiveDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load integration groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await authFetch('/api/integration-groups?exclude_analysis=true');
        if (response.ok) {
          const result = await response.json();
          const groups = Array.isArray(result) ? result : (result.data || result.groups || []);
          setIntegrationGroups(groups);
        }
      } catch (err) {
        console.error('Error loading integration groups:', err);
      }
    };
    fetchGroups();
  }, []);

  // Load dashboard data when group changes
  useEffect(() => {
    if (!selectedGroupId) {
      setData(null);
      return;
    }
    loadDashboardData();
  }, [selectedGroupId]);

  const loadDashboardData = async () => {
    if (!selectedGroupId) return;

    setLoading(true);
    setError(null);
    try {
      let url = '/api/dashboard/executive';
      if (selectedGroupId !== 'ALL') {
        url += `?integration_group_id=${encodeURIComponent(selectedGroupId)}`;
      }

      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to load executive dashboard data');
      }
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num || 0);
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseInt(num) : num;
    return new Intl.NumberFormat('en-US').format(n || 0);
  };

  const getDispositionColor = (disposition: string) => {
    switch (disposition) {
      case 'Retire': return '#ef4444';
      case 'Re-Platform': return '#f59e0b';
      case 'Re-Design': return '#3b82f6';
      case 'Retain': return '#10b981';
      default: return '#9ca3af';
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      'Created': { bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' },
      'Scheduled': { bg: '#fffbeb', text: '#b45309', border: '#fcd34d' },
      'In-Process': { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' },
      'Completed': { bg: '#ecfdf5', text: '#047857', border: '#6ee7b7' },
      'Deployed': { bg: '#ecfdf5', text: '#047857', border: '#6ee7b7' },
      'In Development': { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' }
    };
    const style = colors[status] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`
      }}>
        {status}
      </span>
    );
  };

  // Empty state
  if (!selectedGroupId) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: 'white' }}>Executive Dashboard</h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>Application Portfolio Rationalization Overview</p>
            </div>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                minWidth: '280px',
                backgroundColor: 'white',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
            >
              <option value="">Select Grouping ID</option>
              <option value="ALL">All Groupings</option>
              {integrationGroups.map(group => (
                <option key={group.group_id} value={group.group_id}>
                  {group.group_id} - {group.group_name}
                </option>
              ))}
            </select>
          </div>

          {/* Empty State Card */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '80px 40px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '32px'
            }}>
              📊
            </div>
            <h2 style={{ margin: '0 0 12px', color: '#1f2937', fontSize: '24px', fontWeight: 600 }}>
              Select an Integration Grouping
            </h2>
            <p style={{ color: '#6b7280', margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              Choose a grouping from the dropdown above to view comprehensive portfolio analytics and insights.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '80px 40px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p style={{ color: '#6b7280', margin: 0 }}>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
            <button
              onClick={loadDashboardData}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate totals for disposition chart
  const dispositionData = data.portfolioByDisposition.map(item => ({
    label: item.disposition,
    value: parseInt(item.app_count) || 0,
    color: getDispositionColor(item.disposition)
  }));
  const totalAppsInDisposition = dispositionData.reduce((sum, item) => sum + item.value, 0);

  // Calculate overall progress
  const totalApps = data.keyMetrics.totalApplications;
  const assessedPct = totalApps > 0 ? Math.round((data.keyMetrics.completedAssessments / totalApps) * 100) : 0;
  const deployedPct = totalApps > 0 ? Math.round((data.keyMetrics.deployedApps / totalApps) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '40px' }}>
      {/* Header with gradient */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px 24px 120px',
        marginBottom: '-80px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: 'white' }}>Executive Dashboard</h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                Application Portfolio Rationalization Overview
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '14px',
                  minWidth: '280px',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Grouping ID</option>
                <option value="ALL">All Groupings</option>
                {integrationGroups.map(group => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_id} - {group.group_name}
                  </option>
                ))}
              </select>
              <button
                onClick={loadDashboardData}
                style={{
                  padding: '12px 20px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)'
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        {/* Key Metrics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '20px',
          marginBottom: '24px'
        }}>
          {/* Applications */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #3b82f6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937' }}>
                  {formatNumber(data.keyMetrics.totalApplications)}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Total Applications</div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                📱
              </div>
            </div>
          </div>

          {/* APR Models */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #8b5cf6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937' }}>
                  {formatNumber(data.keyMetrics.totalModels)}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>APR Models</div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                🎯
              </div>
            </div>
          </div>

          {/* Integration Groups */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #06b6d4'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937' }}>
                  {formatNumber(data.keyMetrics.totalGroups)}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Integration Groups</div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                🔗
              </div>
            </div>
          </div>

          {/* Deployed */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderLeft: '4px solid #10b981'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937' }}>
                  {formatNumber(data.keyMetrics.deployedApps)}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Deployed</div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                ✅
              </div>
            </div>
          </div>

          {/* Expected Savings */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)'
          }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: 'white' }}>
                {formatCurrency(data.keyMetrics.expectedSavings2Yr)}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>Expected 2-Year Savings</div>
            </div>
          </div>
        </div>

        {/* Second Row - Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          {/* Disposition Breakdown */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Portfolio by Disposition
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
              <DonutChart
                data={dispositionData}
                size={140}
                strokeWidth={20}
                centerValue={totalAppsInDisposition}
                centerLabel="Apps"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {dispositionData.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      backgroundColor: item.color
                    }} />
                    <span style={{ fontSize: '13px', color: '#374151' }}>{item.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', marginLeft: 'auto' }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overall Progress */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Overall Progress
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <ProgressRing percentage={assessedPct} size={80} strokeWidth={8} color="#3b82f6" label="Assessed" />
              <ProgressRing percentage={deployedPct} size={80} strokeWidth={8} color="#10b981" label="Deployed" />
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Assessment Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Completed Assessments</span>
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#3b82f6' }}>
                  {formatNumber(data.keyMetrics.completedAssessments)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Applications Deployed</span>
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
                  {formatNumber(data.keyMetrics.deployedApps)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Pending Assessment</span>
                <span style={{ fontSize: '18px', fontWeight: 600, color: '#f59e0b' }}>
                  {formatNumber(totalApps - data.keyMetrics.completedAssessments)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* APR Models Progress */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
            APR Models Lifecycle
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Model Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Apps</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>QA Assessment</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Disposition</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>In Development</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Deployed</th>
                </tr>
              </thead>
              <tbody>
                {data.aprModelsSummary.map((model, idx) => {
                  const total = parseInt(model.app_count) || 0;
                  const qaCompleted = parseInt(model.qa_completed) || 0;
                  const dispCompleted = parseInt(model.disposition_completed) || 0;
                  const inDev = parseInt(model.in_development) || 0;
                  const deployed = parseInt(model.deployed) || 0;
                  const qaPct = total > 0 ? Math.round((qaCompleted / total) * 100) : 0;
                  const dispPct = total > 0 ? Math.round((dispCompleted / total) * 100) : 0;
                  const inDevPct = total > 0 ? Math.round((inDev / total) * 100) : 0;
                  const deployedPct = total > 0 ? Math.round((deployed / total) * 100) : 0;

                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '16px', fontWeight: 500, color: '#1f2937' }}>{model.model_name}</td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>{getStatusBadge(model.status)}</td>
                      <td style={{ padding: '16px', textAlign: 'center', fontWeight: 600 }}>{total}</td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={{ width: '60px', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${qaPct}%`, height: '100%', backgroundColor: '#3b82f6', borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#3b82f6', minWidth: '40px' }}>{qaPct}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={{ width: '60px', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${dispPct}%`, height: '100%', backgroundColor: '#8b5cf6', borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#8b5cf6', minWidth: '40px' }}>{dispPct}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={{ width: '60px', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${inDevPct}%`, height: '100%', backgroundColor: '#f59e0b', borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#f59e0b', minWidth: '40px' }}>{inDevPct}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                          <div style={{ width: '60px', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${deployedPct}%`, height: '100%', backgroundColor: '#10b981', borderRadius: '4px', transition: 'width 0.5s' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: '#10b981', minWidth: '40px' }}>{deployedPct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Row - Benefit Realization & Groups */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Benefit Realization */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Benefit Realization by Group
            </h3>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, background: 'white' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Grouping ID</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Group Name</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>2-Yr Savings</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>ROI %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.benefitSummary.map((item, idx) => {
                    const roiValue = parseFloat(item.roi_percentage) || 0;
                    const savingsValue = parseFloat(item.total_2yr_savings) || 0;
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px', fontWeight: 500, color: '#1f2937' }}>{item.grouping_id || '-'}</td>
                        <td style={{ padding: '12px', color: '#6b7280' }}>{item.group_name || 'Ungrouped'}</td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'right',
                          fontWeight: 600,
                          color: savingsValue >= 0 ? '#10b981' : '#ef4444'
                        }}>
                          {formatCurrency(item.total_2yr_savings)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: roiValue >= 0 ? '#ecfdf5' : '#fef2f2',
                            color: roiValue >= 0 ? '#059669' : '#dc2626'
                          }}>
                            {roiValue.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Integration Groups Progress */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Integration Groups Progress
            </h3>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, background: 'white' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Group</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {data.integrationGroupsSummary.map((group, idx) => {
                    const total = parseInt(group.app_count) || 0;
                    const deployed = parseInt(group.deployed_count) || 0;
                    const progress = total > 0 ? Math.round((deployed / total) * 100) : 0;
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: 500, color: '#1f2937' }}>{group.group_name}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{group.group_id}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{
                                width: `${progress}%`,
                                height: '100%',
                                backgroundColor: progress === 100 ? '#10b981' : '#3b82f6',
                                borderRadius: '4px',
                                transition: 'width 0.5s'
                              }} />
                            </div>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: progress === 100 ? '#10b981' : '#3b82f6',
                              minWidth: '40px'
                            }}>
                              {progress}%
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                            {deployed}/{total} apps deployed
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
